import type { CanvasPosition } from './CanvasContext'

/** Minimal node shape needed to resolve parent-relative canvas coordinates. */
export interface CanvasPositionNode {
  id: string
  position: CanvasPosition
  parentId?: string
}

/** Node shape used when finding a free root-canvas position. */
export interface CanvasPlacementNode extends CanvasPositionNode {
  type?: string | null
  hidden?: boolean
  width?: number | null
  height?: number | null
  style?: {
    width?: string | number
    height?: string | number
  }
}

export type CanvasPositionNodeLookup = (
  nodeId: string,
) => CanvasPositionNode | undefined

function assertFinitePosition(position: CanvasPosition, description: string): void {
  if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) {
    throw new Error(
      `${description} must contain finite x/y coordinates; received (${position.x}, ${position.y})`,
    )
  }
}

/**
 * Resolve a source-relative position into root-canvas coordinates.
 *
 * React Flow stores root-node positions in canvas coordinates, but stores each
 * grouped child's position relative to its parent. Walking the full parentId
 * chain is therefore the only stable conversion for root, grouped, and nested
 * grouped nodes. We deliberately ignore React Flow's derived
 * `positionAbsolute`: controlled node snapshots may not have it, and cached
 * values can be stale immediately after an ancestor moves.
 *
 * @param originalNodeId Source node whose top-left is the relative origin.
 * @param relativePosition Desired offset from the source node's top-left.
 * @param getNode Lookup over the current canvas node snapshot.
 */
export function resolveAbsoluteCanvasPosition(
  originalNodeId: string,
  relativePosition: CanvasPosition,
  getNode: CanvasPositionNodeLookup,
): CanvasPosition {
  assertFinitePosition(relativePosition, 'Relative canvas position')

  const visited = new Set<string>()
  let cursorId: string | undefined = originalNodeId
  let x = relativePosition.x
  let y = relativePosition.y

  while (cursorId) {
    if (visited.has(cursorId)) {
      throw new Error(
        `Cannot resolve absolute canvas position for node "${originalNodeId}": parent cycle detected at "${cursorId}"`,
      )
    }
    visited.add(cursorId)

    const node = getNode(cursorId)
    if (!node) {
      const relation = cursorId === originalNodeId ? 'source node' : 'parent node'
      throw new Error(
        `Cannot resolve absolute canvas position for node "${originalNodeId}": ${relation} "${cursorId}" was not found`,
      )
    }
    assertFinitePosition(node.position, `Canvas node "${cursorId}" position`)

    x += node.position.x
    y += node.position.y
    cursorId = node.parentId
  }

  return { x, y }
}

function parseDimension(
  value: string | number | null | undefined,
  fallback: number,
): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return fallback
}

type CanvasRect = { x: number; y: number; width: number; height: number }
type NodeDimensions = { width: number; height: number }

const NODE_PLACEMENT_GAP = 48

const DEFAULT_NODE_DIMENSIONS: NodeDimensions = { width: 420, height: 360 }

const NODE_DIMENSION_FALLBACKS: Record<string, NodeDimensions> = {
  audio: { width: 315, height: 160 },
  characterDesigner: { width: 400, height: 640 },
  group: { width: 520, height: 360 },
  image: { width: 315, height: 240 },
  imageGenerator: { width: 400, height: 520 },
  locationDesigner: { width: 600, height: 520 },
  nodeGroup: { width: 355, height: 260 },
  objectDesigner: { width: 400, height: 560 },
  scriptWriter: { width: 520, height: 420 },
  soundExpert: { width: 400, height: 420 },
  storyboardGenerator: { width: 420, height: 520 },
  video: { width: 315, height: 240 },
  videoEditor: { width: 420, height: 520 },
  videoGenerator: { width: 400, height: 640 },
}

function estimateNodeDimensions(
  nodeType: string | null | undefined,
  node?: CanvasPlacementNode,
): NodeDimensions {
  const fallback = nodeType
    ? (NODE_DIMENSION_FALLBACKS[nodeType] ?? DEFAULT_NODE_DIMENSIONS)
    : DEFAULT_NODE_DIMENSIONS
  return {
    width: parseDimension(node?.width ?? node?.style?.width, fallback.width),
    height: parseDimension(node?.height ?? node?.style?.height, fallback.height),
  }
}

function nodeToPlacementRect(
  node: CanvasPlacementNode,
  nodeById: Map<string, CanvasPlacementNode>,
  positionCache: Map<string, CanvasPosition>,
): CanvasRect {
  let pos = positionCache.get(node.id)
  if (!pos) {
    pos = resolveAbsoluteCanvasPosition(
      node.id,
      { x: 0, y: 0 },
      (nodeId) => nodeById.get(nodeId),
    )
    positionCache.set(node.id, pos)
  }
  const size = estimateNodeDimensions(node.type, node)
  return { x: pos.x, y: pos.y, width: size.width, height: size.height }
}

function rectsOverlap(a: CanvasRect, b: CanvasRect, gap = NODE_PLACEMENT_GAP): boolean {
  return (
    a.x - gap < b.x + b.width &&
    a.x + a.width + gap > b.x &&
    a.y - gap < b.y + b.height &&
    a.y + a.height + gap > b.y
  )
}

/**
 * Find the nearest free root-canvas position around a preferred point.
 *
 * Existing grouped nodes are compared in absolute coordinates so callers can
 * use this for both root and grouped source nodes without stacking new nodes.
 */
export function findNonOverlappingCanvasPosition(
  nodeType: string,
  preferredPosition: CanvasPosition,
  existingNodes: CanvasPlacementNode[],
): CanvasPosition {
  const nodeById = new Map(existingNodes.map((node) => [node.id, node]))
  const positionCache = new Map<string, CanvasPosition>()
  const existingRects = existingNodes
    .filter((node) => !node.hidden)
    .map((node) => nodeToPlacementRect(node, nodeById, positionCache))
  const size = estimateNodeDimensions(nodeType)
  const stepX = size.width + NODE_PLACEMENT_GAP
  const stepY = size.height + NODE_PLACEMENT_GAP

  for (let radius = 0; radius <= 12; radius++) {
    for (let row = -radius; row <= radius; row++) {
      for (let col = -radius; col <= radius; col++) {
        if (radius > 0 && Math.max(Math.abs(row), Math.abs(col)) !== radius) continue
        const candidate = {
          x: preferredPosition.x + col * stepX,
          y: preferredPosition.y + row * stepY,
        }
        const candidateRect = { ...candidate, width: size.width, height: size.height }
        if (!existingRects.some((rect) => rectsOverlap(candidateRect, rect))) {
          return candidate
        }
      }
    }
  }

  const rightmostEdge = existingRects.reduce(
    (edge, rect) => Math.max(edge, rect.x + rect.width),
    preferredPosition.x - NODE_PLACEMENT_GAP,
  )
  return {
    x: rightmostEdge + NODE_PLACEMENT_GAP,
    y: preferredPosition.y,
  }
}
